import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Surface, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, CheckCircle2, Trash2, Search, Printer, Info } from "lucide-react";
import { toast } from "sonner";

const F = ({ label, className = "", children }) => (
  <label className={`text-sm ${className}`}>
    <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

const emptyForm = () => ({
  source_type: "warehouse", source_id: "",
  dest_type: "branch", dest_id: "",
  transfer_date: new Date().toISOString().slice(0, 10),
  transporter: "", vehicle_no: "", lr_no: "", eway_bill_no: "",
  notes: "",
  lines: [],
});

export default function StockTransferEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;

  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState("draft");
  const [transferNo, setTransferNo] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [businessGstin, setBusinessGstin] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerRows, setPickerRows] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const pickerAbortRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      const [ws, bs, bz] = await Promise.all([
        api.get("/production/warehouses"),
        api.get("/sales/branches"),
        api.get("/sales/business-settings"),
      ]);
      setWarehouses(ws.data || []);
      setBranches(bs.data || []);
      setBusinessGstin((bz.data || {}).gstin || null);
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/sales/stock-transfers/${id}`).then((r) => {
      const d = r.data;
      setStatus(d.status);
      setTransferNo(d.transfer_no);
      setForm({
        source_type: d.source?.type || "warehouse",
        source_id: d.source?.id || "",
        dest_type: d.dest?.type || "branch",
        dest_id: d.dest?.id || "",
        transfer_date: d.transfer_date,
        transporter: d.transporter || "",
        vehicle_no: d.vehicle_no || "",
        lr_no: d.lr_no || "",
        eway_bill_no: d.eway_bill_no || "",
        notes: d.notes || "",
        lines: d.lines || [],
      });
    }).catch(() => toast.error("Failed to load transfer"));
  }, [id]);

  const readOnly = status !== "draft";

  // Options — support all directions (warehouse↔warehouse, warehouse↔branch, branch↔branch)
  const sourceOptions = form.source_type === "warehouse" ? warehouses : branches;
  const destOptions = form.dest_type === "warehouse" ? warehouses : branches;

  // GSTIN preview
  const getLocGstin = (type, locId) => {
    if (type === "warehouse") return businessGstin;
    const b = branches.find((x) => x.id === locId);
    return b ? b.gstin : null;
  };
  const srcGstin = getLocGstin(form.source_type, form.source_id);
  const dstGstin = getLocGstin(form.dest_type, form.dest_id);
  const sameGstin = srcGstin && dstGstin && srcGstin === dstGstin;

  // Stock picker
  const searchPicker = useCallback(async (q) => {
    if (!form.source_id) { setPickerRows([]); return; }
    if (pickerAbortRef.current) pickerAbortRef.current.abort?.();
    const ctrl = new AbortController(); pickerAbortRef.current = ctrl;
    setPickerLoading(true);
    try {
      const r = await api.get("/sales/location-stock-picker", {
        params: { location_type: form.source_type, location_id: form.source_id, search: q, limit: 40 },
        signal: ctrl.signal,
      });
      setPickerRows(r.data || []);
    } catch (e) { if (e.name !== "CanceledError") toast.error("Search failed"); }
    finally { setPickerLoading(false); }
  }, [form.source_type, form.source_id]);

  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(() => searchPicker(pickerQuery), 200);
    return () => clearTimeout(t);
  }, [pickerQuery, pickerOpen, searchPicker]);

  const addLine = (row) => {
    if (form.lines.some((l) => l.product_id === row.product_id)) return toast.error("Already added — increase the qty instead");
    setForm((f) => ({ ...f, lines: [...f.lines, {
      product_id: row.product_id, product_name: row.product_name, product_sku: row.product_sku,
      hsn_code: row.hsn_code || "", qty: 1, _avail_qty: row.qty,
    }] }));
    setPickerQuery(""); setPickerOpen(false);
  };
  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((ln, k) => k === i ? { ...ln, ...patch } : ln) }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, k) => k !== i) }));

  const totalQty = form.lines.reduce((s, ln) => s + (Number(ln.qty) || 0), 0);
  const canSave = form.source_id && form.dest_id && form.lines.length > 0 && sameGstin;

  const save = async () => {
    if (!canSave) return toast.error(!sameGstin ? "Source and destination must share the same GSTIN" : "Fill source, destination and at least one line");
    setSaving(true);
    try {
      const payload = {
        ...form,
        lines: form.lines.map((ln) => ({
          product_id: ln.product_id, product_name: ln.product_name, product_sku: ln.product_sku,
          hsn_code: ln.hsn_code || null, qty: Number(ln.qty),
        })),
      };
      const r = isNew ? await api.post("/sales/stock-transfers", payload) : await api.put(`/sales/stock-transfers/${id}`, payload);
      toast.success(isNew ? "Draft saved" : "Draft updated");
      if (isNew) nav(`/sales/stock-transfers/${r.data.id}/edit`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!id) return toast.error("Save the draft first");
    if (!window.confirm("Approve this transfer? A delivery challan number will be assigned and stock will be moved out from the source.")) return;
    setApproving(true);
    try {
      const r = await api.post(`/sales/stock-transfers/${id}/approve`);
      toast.success(`Approved as ${r.data.transfer_no}`);
      setStatus("approved"); setTransferNo(r.data.transfer_no);
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setApproving(false); }
  };
  const markReceived = async () => {
    if (!window.confirm("Mark as received? Destination stock will be added.")) return;
    try {
      await api.post(`/sales/stock-transfers/${id}/receive`);
      toast.success("Received — destination stock updated");
      setStatus("received");
    } catch (e) { toast.error(e?.response?.data?.detail || "Receive failed"); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this transfer? Any stock movements will be reversed.")) return;
    try { await api.post(`/sales/stock-transfers/${id}/cancel`); toast.success("Cancelled"); setStatus("cancelled"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => nav("/sales/stock-transfers")} className="gap-1.5" data-testid="tr-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h2 className="font-heading font-bold text-lg text-gray-900">
          {isNew ? "New Stock Transfer" : (transferNo || `Draft #${id?.slice(0, 8)}`)}
        </h2>
        <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${status === "draft" ? "bg-gray-100 text-gray-700 border-gray-200" : status === "approved" ? "bg-blue-50 text-blue-800 border-blue-200" : status === "received" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>{status}</span>
        <div className="ml-auto flex gap-2">
          {["approved", "received", "cancelled"].includes(status) && transferNo && (
            <a href={`/sales/stock-transfers/${id}/challan`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-1.5" data-testid="tr-print-btn"><Printer className="h-4 w-4" /> Delivery Challan</Button>
            </a>
          )}
          {status !== "cancelled" && !isNew && (
            <Button variant="outline" onClick={cancel} className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50" data-testid="tr-cancel-btn">Cancel</Button>
          )}
          {status === "approved" && (
            <Button className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" onClick={markReceived} data-testid="tr-receive-btn">Mark Received</Button>
          )}
          {status === "draft" && (
            <>
              <Button variant="outline" onClick={save} disabled={saving} className="gap-1.5" data-testid="tr-save-btn"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Draft"}</Button>
              {!isNew && (
                <Button className="bg-blue-700 hover:bg-blue-800 gap-1.5" onClick={approve} disabled={approving || !canSave} data-testid="tr-approve-btn"><CheckCircle2 className="h-4 w-4" /> {approving ? "Approving…" : "Approve & Issue Challan"}</Button>
              )}
            </>
          )}
        </div>
      </div>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Route</h3>
        <div className="grid grid-cols-4 gap-4">
          <F label="Source Type">
            <select value={form.source_type} onChange={(e) => { set("source_type", e.target.value); set("source_id", ""); setForm((f) => ({ ...f, lines: [] })); }} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" disabled={readOnly} data-testid="tr-src-type">
              <option value="warehouse">HQ Warehouse</option>
              <option value="branch">Branch / Franchise</option>
            </select>
          </F>
          <F label="Source *">
            <select value={form.source_id} onChange={(e) => { set("source_id", e.target.value); setForm((f) => ({ ...f, lines: [] })); }} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" disabled={readOnly} data-testid="tr-src">
              <option value="">— Pick source —</option>
              {sourceOptions.map((x) => (
                <option key={x.id} value={x.id}>
                  {form.source_type === "warehouse" ? "🏭 " : (x.branch_type === "franchise" ? "🏪 " : "🏢 ")}
                  {x.name}{x.gstin ? ` (${x.gstin.slice(0, 5)}…)` : ""}
                </option>
              ))}
            </select>
            {srcGstin && <div className="text-[10px] text-gray-500 font-mono mt-1">GSTIN: {srcGstin}</div>}
          </F>
          <F label="Destination Type">
            <select value={form.dest_type} onChange={(e) => { set("dest_type", e.target.value); set("dest_id", ""); }} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" disabled={readOnly} data-testid="tr-dest-type">
              <option value="warehouse">HQ Warehouse</option>
              <option value="branch">Branch / Franchise</option>
            </select>
          </F>
          <F label="Destination *">
            <select value={form.dest_id} onChange={(e) => set("dest_id", e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" disabled={readOnly} data-testid="tr-dest">
              <option value="">— Pick destination —</option>
              {destOptions.filter((b) => !(b.id === form.source_id && form.dest_type === form.source_type)).map((x) => (
                <option key={x.id} value={x.id}>
                  {form.dest_type === "warehouse" ? "🏭 " : (x.branch_type === "franchise" ? "🏪 " : "🏢 ")}
                  {x.name}{x.gstin ? ` (${x.gstin.slice(0, 5)}…)` : ""}
                </option>
              ))}
            </select>
            {dstGstin && <div className="text-[10px] text-gray-500 font-mono mt-1">GSTIN: {dstGstin}</div>}
          </F>
          <F label="Transfer Date"><Input type="date" value={form.transfer_date} onChange={(e) => set("transfer_date", e.target.value)} disabled={readOnly} data-testid="tr-date" /></F>
          <F label="Transporter"><Input value={form.transporter} onChange={(e) => set("transporter", e.target.value)} disabled={readOnly} data-testid="tr-transporter" /></F>
          <F label="Vehicle #"><Input value={form.vehicle_no} onChange={(e) => set("vehicle_no", e.target.value.toUpperCase())} className="font-mono" disabled={readOnly} data-testid="tr-vehicle" /></F>
          <F label="LR # / E-way Bill #">
            <div className="flex gap-2">
              <Input value={form.lr_no} onChange={(e) => set("lr_no", e.target.value)} placeholder="LR" disabled={readOnly} className="flex-1 font-mono" data-testid="tr-lr" />
              <Input value={form.eway_bill_no} onChange={(e) => set("eway_bill_no", e.target.value)} placeholder="E-way" disabled={readOnly} className="flex-1 font-mono" data-testid="tr-eway" />
            </div>
          </F>
          <F label="Notes" className="col-span-4"><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} disabled={readOnly} data-testid="tr-notes" /></F>
        </div>

        {form.source_id && form.dest_id && !sameGstin && (
          <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-800 flex items-start gap-1.5" data-testid="tr-gstin-mismatch">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Source GSTIN <span className="font-mono">{srcGstin || "—"}</span> and destination GSTIN <span className="font-mono">{dstGstin || "—"}</span> do not match. Stock transfers require the same GSTIN entity.
          </div>
        )}
      </Surface>

      <Surface padded={false}>
        <div className="p-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
          <h3 className="font-heading font-semibold text-sm">Line Items</h3>
          {!readOnly && (
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <Input value={pickerQuery} onFocus={() => setPickerOpen(true)} onChange={(e) => { setPickerQuery(e.target.value); setPickerOpen(true); }} onBlur={() => setTimeout(() => setPickerOpen(false), 200)}
                placeholder={form.source_id ? `Search stock at ${sourceOptions.find((x) => x.id === form.source_id)?.name || "source"}…` : "Pick source first"}
                disabled={!form.source_id} className="pl-8" data-testid="tr-picker-search" />
              {pickerOpen && form.source_id && (
                <div className="absolute z-40 mt-1 left-0 right-0 max-h-80 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
                  {pickerLoading && <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>}
                  {!pickerLoading && pickerRows.length === 0 && <div className="px-3 py-3 text-xs text-gray-500">{pickerQuery ? "No matches." : "Type to search."}</div>}
                  {pickerRows.map((r) => (
                    <button key={r.product_id} onMouseDown={(e) => e.preventDefault()} onClick={() => addLine(r)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 text-left border-b border-gray-100 last:border-0" data-testid={`tr-picker-row-${r.product_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.product_name}</div>
                        <div className="text-[11px] text-gray-500 font-mono">{r.product_sku || "—"}{r.hsn_code ? ` · HSN ${r.hsn_code}` : ""}</div>
                      </div>
                      <div className="text-right text-[11px] text-gray-500">Avail <span className="font-semibold tabular-nums text-gray-800">{r.qty}</span></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <span className="text-xs text-gray-500 ml-auto">{form.lines.length} line(s) · total qty <span className="font-semibold tabular-nums">{formatINR(totalQty).replace("₹", "")}</span></span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
              <th className="px-3 py-2 w-8">#</th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2 w-24">HSN</th>
              <th className="px-3 py-2 w-32 text-right">Qty</th>
              {!readOnly && <th className="px-3 py-2 w-8"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {form.lines.length === 0 && (
              <tr><td colSpan={readOnly ? 4 : 5} className="px-3 py-8 text-center text-gray-400">No lines. {form.source_id ? "Search source stock above." : "Pick a source first."}</td></tr>
            )}
            {form.lines.map((ln, i) => (
              <tr key={ln.product_id} data-testid={`tr-line-${ln.product_id}`}>
                <td className="px-3 py-2 text-xs text-gray-500">{i + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{ln.product_name}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{ln.product_sku || "—"}</div>
                </td>
                <td className="px-3 py-2">
                  <input value={ln.hsn_code || ""} onChange={(e) => setLine(i, { hsn_code: e.target.value })} className="h-7 w-20 rounded border border-gray-200 px-2 text-xs font-mono" disabled={readOnly} placeholder="HSN" data-testid={`tr-line-hsn-${ln.product_id}`} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Input type="number" step="0.001" min="0" value={ln.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="h-8 w-24 text-right tabular-nums font-mono ml-auto" disabled={readOnly} data-testid={`tr-line-qty-${ln.product_id}`} />
                  {ln._avail_qty != null && <div className="text-[10px] text-gray-400 mt-0.5">avail {ln._avail_qty}</div>}
                </td>
                {!readOnly && (
                  <td className="px-3 py-2 text-right">
                    <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center" onClick={() => removeLine(i)} data-testid={`tr-line-del-${ln.product_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Surface>
    </div>
  );
}
